//Hooks
import { get } from 'svelte/store';
// Formating
import { format } from 'svelte-i18n';
//Helpers
import { addDate, dateFormat, startsWeekOnSun } from '../../helpers/date_helper';
import { checkForStatisticError, getChartSensorName } from './chart_helper';
import { mathEpsilon } from '../../helpers/utilities';
import { isPremiumUser } from 'src/helpers/user_helpers';
import { home_data } from 'src/stores/account/user';
import { app, os } from 'src/stores/session/native-bridge';
import { is_GBLE } from 'src/helpers/interface_helpers';
//Types
import type { ChartDataInterface, RangeOptionPeriod } from '../../types/Chart';
import type { Device } from 'src/types/Device';

export interface rangeOptionInterface {
	period: RangeOptionPeriod;
	timeRange: string;
	timeLabelFormat: string;
	enabled: boolean;
}

export interface chartControlsDataInterface {
	time: { from: Date; to: Date };
	format: string;
	rangeOption: rangeOptionInterface;
	currentPeriod: string;
}

export const rangeOptions: Array<rangeOptionInterface> = [
	{
		period: '24_hours',
		timeRange: '24h',
		timeLabelFormat: 'day',
		enabled: true,
	},
	{
		period: 'day',
		timeRange: '1d',
		timeLabelFormat: 'day',
		enabled: true,
	},
	{
		period: 'hour',
		timeRange: '1h',
		timeLabelFormat: 'hour',
		enabled: true,
	},

	{
		period: '7_days',
		timeRange: '7d',
		timeLabelFormat: 'week',
		enabled: true,
	},

	{
		period: 'week',
		timeRange: '1w',
		timeLabelFormat: 'week',
		enabled: true,
	},
	{
		period: '30_days',
		timeRange: '30d',
		timeLabelFormat: 'month',
		enabled: true,
	},
	{
		period: 'month',
		timeRange: '1m',
		timeLabelFormat: 'month',
		enabled: true,
	},
	{
		period: '12_months',
		timeRange: '12m',
		timeLabelFormat: 'year',
		enabled: true,
	},
	{
		period: 'year',
		timeRange: '1y',
		timeLabelFormat: 'year',
		enabled: true,
	},
	{
		period: 'billing_date',
		timeRange: '1m',
		timeLabelFormat: 'month',
		enabled: true,
	},
	{
		period: 'custom',
		timeRange: 'custom',
		timeLabelFormat: 'custom',
		enabled: true,
	},
];

/**
 *
 * @param date : Date => current date we calculate from
 * @param rangeOption : rangeOptionInterface
 * @param action : string | [subtract, add, init]
 * @returns {from: Date, to: Date}
 */
export function rangeDates(
	date: Date,
	rangeOption: rangeOptionInterface,
	action: 'subtract' | 'add' | 'init' | 'click' = 'init',
	subtractOneSecond = false,
): { from: Date; to: Date } {
	const init =
			action == 'init' && /^(24_hours|7_days|30_days|12_months)$/.test(rangeOption.period)
				? -1
				: 0,
		actions = { subtract: -1, add: 1, init, click: 0 },
		t = rangeOption.timeRange.split(/(\d+)/),
		timeValue = Number(t[0] + t[1]),
		timeRange = t[2],
		startWeekOnSun = /^w(eek)?/.test(timeRange) && startsWeekOnSun();
	let from = addDate(
		date,
		timeRange,
		timeValue * actions[action],
		!/^(billing_date)$/.test(rangeOption.period),
		startWeekOnSun,
	);
	if (init == -1) from = addDate(from, timeRange, 1, false, startWeekOnSun);
	let to = addDate(from, timeRange, timeValue, false, startWeekOnSun);
	if (subtractOneSecond) to = addDate(to, 's', -1);
	return { from, to };
}

function stringToUtf16LeBase64(text: string): string {
	let binary = '';
	for (let i = 0; i < text.length; i++) {
		const code = text.charCodeAt(i);
		binary += String.fromCharCode(code & 0xff);
		binary += String.fromCharCode(code >> 8);
	}
	return btoa(binary);
}

export function generateCSVStatistic(
	statsData: ChartDataInterface,
	filename: string,
	date: { from: Date; to: Date },
) {
	filename =
		`${filename || ''}_${statsData.name}_${statsData?.sensorID ? getChartSensorName(statsData) + '_' : ''}` +
		`${dateFormat(date.from, 'd_m_Y_H') + 'h__' + dateFormat(addDate(date.to, 's'), 'd_m_Y_H') + 'h'}`;
	filename = `${filename.replace(/([^a-zа-я\d_]+)/gi, '_')}.csv`.toLowerCase();
	const newLine = '\n',
		sep = ',',
		$format = get(format);
	let csvContent = '';
	statsData.series.map((s) => {
		if (!s?.prevPeriod) {
			const name = s.name.replace(/_\d+$/, '').replace(/^reversed$/, 'total_returned');
			csvContent += ` ${$format('chart_legend_' + name, {
				default: s.name,
			})}${newLine}`;
			csvContent += ` ${$format('time', { default: 'Time' })}${sep} ${statsData.units} ${newLine} `;
			s.data.map((d) => {
				const value = checkForStatisticError(d.value) ? 'N/A' : mathEpsilon(d.value, 3);
				csvContent +=
					' ' + dateFormat(Number(d.dt), 'd/m/Y H:i', true) + ` ${sep} ${value} ${newLine} `;
			});
			csvContent += newLine;
		}
	});
	// Add BOM once at the beginning to ensure proper UTF-8 encoding for Excel
	csvContent = '\uFEFF' + csvContent;
	const a = document.createElement('a');
	if (get(app) && get(os) !== 'IOS') {
		const base64Csv = stringToUtf16LeBase64(csvContent);
		a.setAttribute('href', `data:text/csv;charset=utf-16le;base64,${base64Csv}`);
		a.setAttribute('download', filename);
		a.setAttribute('data-downloadurl', `text/csv:${filename}:data:text/csv;base64,${base64Csv}`);
		a.click();
	} else {
		a.setAttribute('target', '_blank');
		a.setAttribute('download', filename);
		const csv = new Blob([csvContent], { type: 'text/csv;charset=utf-8' }),
			windowURL = window.webkitURL || window.URL,
			url = windowURL.createObjectURL(csv);
		a.setAttribute('href', url);
		a.click();
		windowURL.revokeObjectURL(url);
	}
}

export function getInitLoadedPeriodStatistics(rangeOpt: rangeOptionInterface[]): rangeOptionInterface {
	let rangeOption: rangeOptionInterface = rangeOpt[0];
	const initPeriodKey = get(home_data)?.init_loaded_period_statistics;
	if (initPeriodKey) {
		rangeOption = rangeOpt.find((i) => i.period == initPeriodKey);
		if (!rangeOption?.enabled) rangeOption = rangeOpt[0];
	}
	return rangeOption;
}

export function isPrevPeriodComparisonEnabled(): boolean {
	return get(home_data)?.prev_period_comparison?.value;
}

export function getBillingDate(): Date {
	const today = new Date(),
		billingDate = get(home_data)?.billing_date || 1;
	let date = new Date();
	if (today.getDate() < billingDate) {
		date = addDate(today, 'm', -1);
	}
	date.setDate(billingDate);
	return date;
}

export function getEnabledHourControl(): boolean {
	return isPremiumUser();
}

export const isBleStatisticsDeviceToUTC = (
	device: Device,
	period: rangeOptionInterface['period'],
): boolean => {
	return is_GBLE(device) && /^(24_hours|day|hour)$/.test(period);
};
